import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { paths } from '@common/types';
import { OTPInput } from '@components';
import { setUserData } from '@common/auth';
import useUserStore from 'store/user.store';
import { Box, Link, Paper, Typography } from '@mui/material';
import { postRequest } from '@config/axios/axios.config';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { HttpErrorMessage } from '../common';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { setTokens } from '@store';

export const LoginMFA = () => {
  const location = useLocation();
  const { state } = location;
  const { mfaType, maskedPhoneNumber } = state ?? {};
  const [otpValue, setOTPValue] = useState<string>('');
  const [hasError, setHasError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const { setUser } = useUserStore();

  if (!token) {
    navigate('/');
  }

  const handleOnChangeOtpValue = (newValue: string) => {
    setOTPValue(newValue);
  };

  const resendMFARequest = async () => {
    try {
      await postRequest<
        paths['/auth/login/mfa/resend']['post']['requestBody']['content']['application/json'],
        paths['/auth/login/mfa/resend']['post']['responses']['200']['content']['application/json']
      >('/auth/login/mfa/resend', {
        token,
      });

      toast.success('Code sent successfully');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleResendCode = () => {
    resendMFARequest();
  };

  useEffect(() => {
    async function fetchMyAPI() {
      try {
        setHasError(null);

        const response = await postRequest<
          paths['/auth/login/mfa/verify']['post']['requestBody']['content']['application/json'],
          paths['/auth/login/mfa/verify']['post']['responses']['200']['content']['application/json']
        >('/auth/login/mfa/verify', {
          token,
          secret: otpValue,
        });

        const data = response.data;

        setTokens({
          token: data.token,
          refreshToken: data.refresh_token,
        });

        setUserData(data.token, setUser);

        navigate('/');
      } catch (error) {
        toast.error(formatApiError(error));
        setHasError(formatApiError(error));
      }
    }

    if (otpValue.length === 6 && !otpValue.includes(' ')) {
      fetchMyAPI();
    }
  }, [otpValue]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.5rem',
        width: '28rem',
        height: '100%',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '1.5rem',
          gap: '16px',
          width: '100%',
          borderRadius: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            padding: 0,
          }}
        >
          <VerifiedUserIcon sx={{ fontSize: '3rem' }} color='primary' />
          <Typography
            sx={{ fontWeight: '600', fontSize: '1.5rem', color: 'primary' }}
          >
            Verify your account
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 'regular',
              paddingY: '16px',
              textAlign: 'center',
            }}
          >
            {mfaType === 'sms'
              ? `Enter your 6-digit Bridge verification code sent to ${maskedPhoneNumber}.`
              : 'Open your authentication app, and enter the one-time password shown to verify your account.'}
          </Typography>
        </Box>

        {hasError && <HttpErrorMessage message={hasError} />}

        <OTPInput
          length={6}
          value={otpValue}
          onChange={handleOnChangeOtpValue}
        />

        <Link
          component='button'
          variant='body2'
          onClick={handleResendCode}
          sx={{
            fontSize: '1rem',
            fontWeight: 'regular',
            marginTop: 1,
          }}
        >
          Resend code
        </Link>

        <Typography
          sx={{
            fontSize: '0.75rem',
            color: theme => theme.palette.semiBlack.main,
            textAlign: 'center',
            width: '100%',
            paddingTop: '1rem',
          }}
        >
          If you're unable to authenticate your account, please contact your
          system administrator.
        </Typography>
      </Paper>
    </Box>
  );
};
